import { CircleHelp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DEFAULT_VOTE_ANONYMIZATION,
  DEFAULT_VOTE_VISIBILITY,
  VOTE_ANONYMIZATION_OPTIONS,
  VOTE_VISIBILITY,
  VOTE_VISIBILITY_OPTIONS,
  resolveVoteAnonymization,
  resolveVoteVisibility,
} from "../../lib/vote-visibility";

const HIDE_VOTE_LIST_TOOLTIP =
  "Participants still see vote totals. This hides the participant list showing who has or has not voted. Organizer visibility is unchanged.";

export function VotePrivacySettings({
  expanded = false,
  onExpandedChange,
  voteVisibility = DEFAULT_VOTE_VISIBILITY,
  onVoteVisibilityChange,
  hideVoterIdentities = false,
  onHideVoterIdentitiesChange,
  voteAnonymization = DEFAULT_VOTE_ANONYMIZATION,
  onVoteAnonymizationChange,
  voteAnonymizationLocked = false,
  calendarSyncEnabled,
  onCalendarSyncEnabledChange,
  summaryItems = [],
  children = null,
  className = "",
}) {
  const normalizedVoteVisibility = resolveVoteVisibility(voteVisibility);
  const normalizedVoteAnonymization = resolveVoteAnonymization(voteAnonymization);
  const voteVisibilityOption = VOTE_VISIBILITY_OPTIONS.find(
    (option) => option.value === normalizedVoteVisibility
  );
  const voteAnonymizationOption = VOTE_ANONYMIZATION_OPTIONS.find(
    (option) => option.value === normalizedVoteAnonymization
  );
  const hideVoterIdentitiesLocked = normalizedVoteVisibility === VOTE_VISIBILITY.FULL;
  // When Identity labels anonymize voters, identities are already withheld until
  // reveal, so the separate "Hide identity of voters" checkbox is redundant.
  const anonymizationActive = normalizedVoteAnonymization !== DEFAULT_VOTE_ANONYMIZATION;
  const showHideVoterIdentitiesControl = !hideVoterIdentitiesLocked && !anonymizationActive;
  const hideVoterIdentitiesLabel =
    normalizedVoteVisibility === VOTE_VISIBILITY.HIDDEN
      ? "Hide identity of voters from participants"
      : "Hide identity of voters until votes revealed";

  return (
    <div
      className={`rounded-xl border border-border bg-card p-3 ${className}`.trim()}
    >
      <button
        type="button"
        onClick={() => onExpandedChange?.(!expanded)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="text-sm font-semibold text-card-foreground">
          Advanced settings
        </span>
        <span className="text-xs font-semibold text-muted-foreground">
          {expanded ? "Hide" : "Show"}
        </span>
      </button>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-card-foreground">
        <span className="rounded-full border border-border bg-muted px-2 py-0.5">
          Vote privacy: {voteVisibilityOption?.label || "Vote visibility"}
        </span>
        {!hideVoterIdentitiesLocked && !anonymizationActive && hideVoterIdentities ? (
          <span className="rounded-full border border-border bg-muted px-2 py-0.5">
            Participant vote list hidden
          </span>
        ) : null}
        <span className="rounded-full border border-border bg-muted px-2 py-0.5">
          Identity labels: {voteAnonymizationOption?.label || "No anonymization"}
        </span>
        {onCalendarSyncEnabledChange != null ? (
          <span className={`rounded-full border px-2 py-0.5 ${
            calendarSyncEnabled
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
              : "border-border bg-muted"
          }`}>
            Calendar sync: {calendarSyncEnabled ? "On" : "Off"}
          </span>
        ) : null}
        {summaryItems.map((item) => (
          <span key={item} className="rounded-full border border-border bg-muted px-2 py-0.5">
            {item}
          </span>
        ))}
      </div>
      {expanded ? (
        <div className="mt-2 space-y-2 rounded-lg border border-border bg-muted p-2.5">
          <div className="grid gap-2.5 rounded-md border border-border bg-card p-2.5 sm:grid-cols-[minmax(0,13.5rem)_minmax(0,1fr)]">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-card-foreground">Vote privacy</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {voteVisibilityOption?.description}
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <div className="w-full lg:max-w-xs">
                  <Select
                    value={normalizedVoteVisibility}
                    onValueChange={(value) => onVoteVisibilityChange?.(resolveVoteVisibility(value))}
                  >
                    <SelectTrigger aria-label="Vote visibility" className="h-9 rounded-lg border-input bg-card px-3 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VOTE_VISIBILITY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {showHideVoterIdentitiesControl ? (
                  <label className="flex min-w-0 items-start gap-2 text-xs text-muted-foreground">
                    <input
                      className="mt-0.5 accent-brand-primary"
                      type="checkbox"
                      checked={hideVoterIdentities}
                      onChange={(event) => onHideVoterIdentitiesChange?.(event.target.checked)}
                    />
                    <span className="inline-flex min-w-0 items-start gap-1 font-semibold leading-snug text-card-foreground">
                      <span>{hideVoterIdentitiesLabel}</span>
                      <span
                        className="inline-flex shrink-0 cursor-help items-center text-muted-foreground"
                        title={HIDE_VOTE_LIST_TOOLTIP}
                        aria-label={HIDE_VOTE_LIST_TOOLTIP}
                      >
                        <CircleHelp className="h-3.5 w-3.5" />
                      </span>
                    </span>
                  </label>
                ) : !hideVoterIdentitiesLocked && anonymizationActive ? (
                  <p className="min-w-0 text-[11px] leading-snug text-muted-foreground">
                    Identity labels already anonymize voters — names show only as aliases, never real names.
                  </p>
                ) : null}
              </div>
              {showHideVoterIdentitiesControl && hideVoterIdentities ? (
                <p className="text-[11px] text-muted-foreground">
                  Participants still see totals, but not the list of who has or has not voted. Organizers still see names.
                </p>
              ) : null}
            </div>
          </div>
          <div className="grid gap-2.5 rounded-md border border-border bg-card p-2.5 sm:grid-cols-[minmax(0,13.5rem)_minmax(0,1fr)]">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-card-foreground">
                Identity labels
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {voteAnonymizationOption?.description}
              </p>
            </div>
            <div className="w-full sm:max-w-xs">
              <Select
                value={normalizedVoteAnonymization}
                onValueChange={(value) => onVoteAnonymizationChange?.(resolveVoteAnonymization(value))}
                disabled={voteAnonymizationLocked}
              >
                <SelectTrigger
                  data-testid="vote-anonymization-select"
                  aria-label="Identity labels"
                  className="h-9 rounded-lg border-input bg-card px-3 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VOTE_ANONYMIZATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {voteAnonymizationLocked ? (
                <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                  Locked — someone has voted under anonymity, so this can't be changed.
                </p>
              ) : null}
            </div>
          </div>
          {onCalendarSyncEnabledChange != null ? (
            <div className="grid gap-2.5 rounded-md border border-border bg-card p-2.5 sm:grid-cols-[minmax(0,13.5rem)_minmax(0,1fr)]">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-card-foreground">
                  Calendar sync
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Automatically sync changes between the Google Calendar event and this poll after finalizing.
                </p>
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 text-xs font-semibold text-card-foreground">
                  <input
                    type="checkbox"
                    className="accent-brand-primary"
                    checked={calendarSyncEnabled}
                    onChange={(event) => onCalendarSyncEnabledChange?.(event.target.checked)}
                  />
                  Enable calendar sync
                </label>
              </div>
            </div>
          ) : null}
          {children}
        </div>
      ) : null}
    </div>
  );
}
